import React, { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { type SidebarMenuItem } from 'app/widgets/sidebar-menu';
import { DIGITAL_ACCOUNT_ROUTES } from '../../../constants/routes';
import { useDigitalContext } from '../../../context/digital';
import { useCustomNavigate } from '../../../utils/customHooks/useCustomNavigate';
import MainLayout from './MainLayout';

interface UnRegisteredLayoutProps {
  isLoading?: boolean;
  user?: {
    company?: {
      title?: string;
      employee?: {
        sign_type?: string;
        method?: string;
        id?: string;
      };
      id?: string;
      inn?: string;
    };
    id?: string;
  };
  mailbox_unread?: number;
}

/**
 * Guest menu. Mirrors the `get_header_menu` payload one-to-one (same ids, order
 * and accordion groups keyed by the parent item's own id) so the sidebar renders
 * the registered and unregistered menus through the same code path. Everything a
 * guest cannot reach yet is marked `deactive`; only the account-opening review
 * and the loan overview point at guest routes.
 */
const unregisteredMenuList = (): SidebarMenuItem[] => {
  return [
    {
      id: 'home-page',
      icon: 'https://static.bankyelo.az/i/internetbanking/ana_sehife.svg',
      title: 'Əsas səhifə',
      path_url: '/api-web/v1/main/home-page',
      path_front: '/az/home-page',
      deactive: true,
    },
    {
      id: 'account_list',
      icon: 'https://static.bankyelo.az/i/internetbanking/kartlar_hesablar.svg',
      title: 'Hesablar və kartlar',
      path_url: '/api-web/v1/main/accounts-card',
      path_front: 'digital-account/review',
      priv: 'accounts',
      deactive: false,
    },
    {
      id: 'request_loan',
      icon: 'https://static.bankyelo.az/i/internetbanking/kartlar_hesablar.svg',
      title: 'Kreditlər',
      path_url: '/api-web/v1/main/loan',
      path_front: 'loan/overview-guest',
      deactive: false,
    },
    {
      id: 'transfer_list',
      icon: 'https://static.bankyelo.az/i/internetbanking/kocurme_odenisler.svg',
      title: 'Köçürmələr',
      deactive: true,
      transfer_list: [
        {
          id: 'payment_acc2acc',
          title: 'Öz hesablarım arası',
          path_url: '/api-web/v1/main/payment_acc2acc',
          path_front: '/az/transfer-page?type=payment_acc2acc',
        },
        {
          id: 'payment_interbank',
          title: 'Bankdaxili',
          path_url: '/api-web/v1/main/payment_interbank',
          path_front: '/az/transfer-page?type=payment_interbank',
        },
        {
          id: 'payment_local_transfer',
          title: 'Ölkədaxili',
          path_url: '/api-web/v1/main/payment_local_transfer',
          path_front: '/az/transfer-page?type=payment_local_transfer',
        },
        {
          id: 'payment_foreign_transfer',
          title: 'Xarici valyutada',
          path_url: '/api-web/v1/main/payment_foreign_transfer',
          path_front: '/az/transfer-page?type=payment_foreign_transfer',
        },
        {
          id: 'payment_conversion',
          title: 'Konvertasiya',
          path_url: '/api-web/v1/main/payment_conversion',
          path_front: '/az/transfer-page?type=payment_conversion',
        },
        {
          id: 'anipay',
          title: 'AniPay',
          path_url: '/api-web/v1/main/ips',
          path_front: '/az/ani-pay/alias/tin',
          icon: 'https://static.bankyelo.az/i/internetbanking/ips.png',
        },
      ],
    },
    {
      id: 'payment_list',
      icon: 'https://static.bankyelo.az/i/internetbanking/kocurme_odenisler.svg',
      title: 'Ödənişlər',
      deactive: true,
      payment_list: [
        {
          id: 'HOP',
          icon: 'https://static.bankyelo.az/i/internetbanking/bank.svg',
          title: 'HÖP',
          path_url: '/api-web/v1/main/credits',
          path_front: '/az/government-payment-portal',
        },
        {
          id: 'payment_budget',
          title: 'Büdcə',
          path_url: '/api-web/v1/main/payment_budget',
          path_front: '/az/transfer-page?type=payment_budget',
        },
        {
          id: 'payment_edv',
          title: 'ƏDV',
          path_url: '/api-web/v1/main/payment_edv',
          path_front: '/az/transfer-page?type=payment_edv',
        },
        {
          id: 'other_payments',
          title: 'Rüsum və digər',
          path_url: '/api-web/v1/main/other_payments',
          path_front: '/az/transfer-page-v2?type=other_payments',
        },
        {
          id: 'preflight_fee',
          title: 'Reysqabağı yoxlama',
          path_url: '/api-web/v1/main/pre-flight-fee',
          path_front: '/az/pre-flight-fee',
        },
      ],
    },
    {
      id: 'signed-operations',
      icon: 'https://static.bankyelo.az/i/internetbanking/imzalanmali_emeliyyatlar.svg',
      title: 'İmzalanmalı əməliyyatlar',
      path_url: '/api-web/v1/main/signed-operations',
      path_front: '/az/signed-operations',
      priv: 'signables',
      deactive: true,
    },
    {
      id: 'payment-orders',
      icon: 'https://static.bankyelo.az/i/internetbanking/serencamlar.svg',
      title: 'Sərəncamlar',
      path_url: '/api-web/v1/main/payment-orders',
      path_front: '/az/payment-orders',
      priv: 'hop_orders',
      deactive: true,
    },
    {
      id: 'templates',
      icon: 'https://static.bankyelo.az/i/internetbanking/sablonlar.svg',
      title: 'Şablonlar',
      path_url: '/api-web/v1/main/get_templates',
      path_front: '/az/templates-page',
      priv: 'transfers',
      deactive: true,
    },
    {
      id: 'salary_list',
      icon: 'https://static.bankyelo.az/i/internetbanking/emekhaqqi.svg',
      title: 'Əmək haqqı',
      deactive: true,
      salary_list: [
        {
          id: 'employee-table',
          title: 'İşçi siyahısı',
          path_url: '/api-web/v1/main/employee-table',
          path_front: '/az/salary?type=employee-table',
        },
        {
          id: 'new-payments',
          title: 'Yeni ödəniş',
          path_url: '/api-web/v1/main/new-payments',
          path_front: '/az/salary?type=new-payments',
        },
        {
          id: 'payments-history',
          title: 'Ödənişlərin tarixçəsi',
          path_url: '/api-web/v1/main/payments-history',
          path_front: '/az/salary?type=payments-history',
        },
      ],
    },
    {
      id: 'salary_card',
      icon: 'https://static.bankyelo.az/i/internetbanking/credit-card-02.svg',
      title: 'Əmək haqqı kartı',
      subtitle: 'yeni',
      deactive: true,
      salary_card: [
        {
          id: 'salary_card',
          title: 'Yeni kart sifarişi',
          path_url: '/api-web/v1/main/salary_cards',
          path_front: '/az/salary-card',
        },
        {
          id: 'salary_cards_prolong',
          title: 'Müddətin uzadılması',
          path_url: '/api-web/v1/main/salary_cards_prolong',
          path_front: '/az/salary-card/extension',
        },
        {
          id: 'salary_cards_statuses',
          title: 'Müraciətlər',
          path_url: '/api-web/v1/main/salary_cards_statuses',
          path_front: '/az/salary-card/applications',
        },
      ],
    },
    {
      id: 'guarantee',
      icon: 'https://static.bankyelo.az/i/internetbanking/qarantiyalar.svg',
      title: 'Qarantiyalar',
      subtitle: 'yeni',
      deactive: true,
      guarantee: [
        {
          id: 'guarantee-v2',
          title: 'Tender qarantiyaları',
          path_url: '/api-web/v1/main/guarantees',
          path_front: '/az/guarantee-v2',
        },
        {
          id: 'loan_list',
          title: 'Digər qarantiyalar',
          path_url: '/api-web/v1/main/guarantee_request',
          path_front: '/az/guarantee/order',
        },
      ],
    },
  ];
};

const UnRegisteredLayout: React.FC<UnRegisteredLayoutProps> = ({
  isLoading = false,
  user,
  mailbox_unread = 0,
}) => {
  const { state } = useDigitalContext();
  const navigate = useCustomNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();

  const updatedUser = {
    ...user,
    company: {
      ...user?.company,
      title: searchParams.get('company_name'),
      employee: {
        ...user?.company?.employee,
        sign_type: 'first',
      },
    },
  };

  const menuList = useMemo(
    () =>
      unregisteredMenuList().map(item => ({
        ...item,
        // Guest routes need the onboarding query string; accordions have no
        // path_front of their own and every disabled row is display-only.
        path_front:
          !item.deactive && item.path_front && queryString
            ? `${item.path_front}?${queryString}`
            : item.path_front,
      })),
    [location.pathname, location.search]
  );

  useEffect(() => {
    if (state.user && !location.pathname.includes(DIGITAL_ACCOUNT_ROUTES.review)) {
      navigate(
        `unregistered/${DIGITAL_ACCOUNT_ROUTES.base}/${DIGITAL_ACCOUNT_ROUTES.review}?${queryString}`
      );
    }
  }, [user]);

  return (
    <MainLayout
      contentSx={
        location.pathname.endsWith(
          `/${DIGITAL_ACCOUNT_ROUTES.base}/${DIGITAL_ACCOUNT_ROUTES.review}`
        )
          ? theme => ({
              minWidth: 0,
              boxSizing: 'border-box',
              [theme.breakpoints.down('md')]: { p: '20px' },
            })
          : undefined
      }
      data={{
        main_menu: menuList,
        user: updatedUser,
        mailbox_unread,
      }}
      isLoading={isLoading}
    />
  );
};

export default UnRegisteredLayout;
